import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, Layers, CalendarHeart, MoreHorizontal,
  Package, Users, Settings, Truck, DollarSign, ChefHat, Globe,
  // PackagePlus, ShoppingBag, TrendingUp — only used by the temporarily-hidden
  // nav items below; re-add when those come back.
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { useCateringPendingUpdatesCount } from '@/hooks/useCatering'
import type { Role } from '@/types/app'
import { useState, useRef, useEffect } from 'react'

interface MobileNavItem {
  label: string
  href: string
  icon: React.ReactNode
  roles: Role[]
  badgeKey?: 'cateringUpdates'
}

const PRIMARY_NAV: MobileNavItem[] = [
  { label: 'Home',    href: '/dashboard',       icon: <LayoutDashboard size={20} />, roles: ['owner','gm','ops','chef','marketing'] },
  { label: 'Products', href: '/products',        icon: <Layers size={20} />,          roles: ['owner','gm','ops','chef','marketing'] },
  { label: 'Catering', href: '/catering',        icon: <CalendarHeart size={20} />,   roles: ['owner','gm','ops','marketing'], badgeKey: 'cateringUpdates' },
  // Temporarily hidden — not deleted. Re-enable by uncommenting (and re-adding the PackagePlus import).
  // { label: 'Stock',    href: '/products/stock',  icon: <PackagePlus size={20} />,     roles: ['owner','gm','ops','chef'] },
]

const MORE_NAV: MobileNavItem[] = [
  { label: 'Catering Pricing', href: '/catering/pricing', icon: <DollarSign size={20} />, roles: ['owner','gm','ops'] },
  { label: 'Web Orders', href: '/web-orders', icon: <Globe size={20} />, roles: ['owner','gm','ops','marketing'] },
  { label: 'Recipes',    href: '/products/recipes', icon: <ChefHat size={20} />,     roles: ['owner','gm','ops','chef'] },
  { label: 'Inventory',  href: '/inventory',       icon: <Package size={20} />,     roles: ['owner','gm','ops','chef'] },
  // Temporarily hidden — not deleted. Re-enable by uncommenting (and re-adding the ShoppingBag/TrendingUp imports).
  // { label: 'Store Supply', href: '/store-supply',  icon: <ShoppingBag size={20} />, roles: ['owner','gm','ops','chef'] },
  { label: 'Suppliers',  href: '/suppliers',       icon: <Truck size={20} />,       roles: ['owner','gm','ops'] },
  // { label: 'Sales',      href: '/sales',           icon: <TrendingUp size={20} />,  roles: ['owner','gm'] },
  { label: 'Team',       href: '/team',            icon: <Users size={20} />,       roles: ['owner','gm'] },
  { label: 'Settings',   href: '/settings',        icon: <Settings size={20} />,    roles: ['owner','gm'] },
]

export function MobileNav() {
  const { user } = useAuthStore()
  const location = useLocation()
  const [moreOpen, setMoreOpen] = useState(false)
  const moreRef = useRef<HTMLDivElement>(null)
  const { data: cateringUpdates = 0 } = useCateringPendingUpdatesCount()
  const badgeCounts: Partial<Record<NonNullable<MobileNavItem['badgeKey']>, number>> = { cateringUpdates }

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false)
    }
    if (moreOpen) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [moreOpen])

  // Close more menu on navigation
  useEffect(() => { setMoreOpen(false) }, [location.pathname])

  const visiblePrimary = PRIMARY_NAV.filter(item => user?.role && item.roles.includes(user.role))
  const visibleMore    = MORE_NAV.filter(item => user?.role && item.roles.includes(user.role))

  const isMoreActive = visibleMore.some(item => location.pathname.startsWith(item.href))

  return (
    <>
      {/* More drawer */}
      {moreOpen && (
        <div ref={moreRef} className="fixed bottom-16 left-0 right-0 z-50 bg-white border-t border-stone-200 shadow-xl rounded-t-2xl px-4 py-3 grid grid-cols-3 gap-2">
          {visibleMore.map(item => {
            const active = location.pathname.startsWith(item.href)
            return (
              <NavLink
                key={item.href}
                to={item.href}
                className={cn(
                  'flex flex-col items-center gap-1 py-3 rounded-xl text-xs font-medium transition-colors',
                  active ? 'bg-brand-50 text-brand-700' : 'text-stone-500 hover:bg-stone-50 hover:text-stone-800'
                )}
              >
                <span className={active ? 'text-brand-600' : 'text-stone-400'}>{item.icon}</span>
                {item.label}
              </NavLink>
            )
          })}
        </div>
      )}

      {/* Bottom bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-stone-200 flex items-stretch h-16 safe-area-pb">
        {visiblePrimary.map(item => {
          const active = location.pathname.startsWith(item.href) && !(moreOpen)
          const badge = item.badgeKey ? badgeCounts[item.badgeKey] : undefined
          return (
            <NavLink
              key={item.href}
              to={item.href}
              className={cn(
                'flex-1 flex flex-col items-center justify-center gap-0.5 text-xs font-medium transition-colors',
                active ? 'text-brand-700' : 'text-stone-400 hover:text-stone-700'
              )}
            >
              <span className={cn('relative', active ? 'text-brand-600' : '')}>
                {item.icon}
                {!!badge && (
                  <span className="absolute -top-1 -right-1.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-brand-500 text-white text-[9px] font-semibold flex items-center justify-center">
                    {badge}
                  </span>
                )}
              </span>
              <span className="text-2xs">{item.label}</span>
            </NavLink>
          )
        })}

        {visibleMore.length > 0 && (
          <button
            onClick={() => setMoreOpen(v => !v)}
            className={cn(
              'flex-1 flex flex-col items-center justify-center gap-0.5 text-xs font-medium transition-colors',
              moreOpen || isMoreActive ? 'text-brand-700' : 'text-stone-400 hover:text-stone-700'
            )}
          >
            <MoreHorizontal size={20} className={moreOpen || isMoreActive ? 'text-brand-600' : ''} />
            <span className="text-2xs">More</span>
          </button>
        )}
      </nav>
    </>
  )
}
